import { NavLink, useLocation, useParams } from "react-router-dom";
import { Icon, type IconName } from "../icons";
import { usePersona, type Persona } from "../../hooks/usePersona";
import { useMe } from "../../hooks/useMe";
import { useAppStore } from "../../state/useAppStore";

interface NavSpec {
  key: string;
  label: string;
  icon: IconName;
  path: string;
  show: ReadonlySet<Persona>;
  section: "work" | "orchestration" | "admin";
}

const ALL: ReadonlySet<Persona> = new Set([
  "owner",
  "operator",
  "staff",
  "approver",
  "reviewer",
  "cxo",
]);

const HANDS_PERSONAS: ReadonlySet<Persona> = new Set([
  "cxo",
  "owner",
  "operator",
  "reviewer",
  "staff",
]);

// Persona-gated nav per Design/chrome.jsx visibility table.
const ITEMS: readonly NavSpec[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: "dashboard",
    path: "dashboard",
    show: ALL,
    section: "work",
  },
  {
    key: "operator",
    label: "My Tasks",
    icon: "actions",
    path: "operator",
    show: ALL,
    section: "work",
  },
  {
    key: "spotted",
    label: "Spotted",
    icon: "actions",
    path: "spotted",
    show: ALL,
    section: "work",
  },
  {
    key: "inbox",
    label: "Inbox",
    icon: "inbox",
    path: "inbox",
    show: new Set<Persona>(["cxo", "operator", "owner"]),
    section: "orchestration",
  },
  {
    // GamePlans groups related plays under a shared execution context.
    // Sits above Plays so the hierarchy reads top-down (GamePlan → Plays);
    // most workspaces will have far more Plays than GamePlans (~80/20).
    key: "gameplans",
    label: "GamePlans",
    icon: "flow",
    path: "gameplans",
    show: ALL,
    section: "orchestration",
  },
  {
    key: "plays",
    label: "Plays",
    icon: "play",
    path: "plays",
    show: ALL,
    section: "orchestration",
  },
  {
    key: "outcomes",
    label: "Outcomes",
    icon: "star",
    path: "outcomes",
    show: new Set<Persona>(["cxo", "operator", "owner", "reviewer", "approver"]),
    section: "orchestration",
  },
  {
    key: "templates",
    label: "Templates",
    icon: "sparkle",
    path: "templates",
    show: new Set<Persona>(["cxo", "operator", "owner"]),
    section: "orchestration",
  },
  {
    key: "people",
    label: "Members",
    icon: "people",
    path: "people",
    show: ALL,
    section: "admin",
  },
  {
    key: "subscription",
    label: "Subscription",
    icon: "check",
    path: "subscription",
    show: new Set<Persona>(["cxo", "owner"]),
    section: "admin",
  },
  {
    key: "settings",
    label: "Settings",
    icon: "settings",
    path: "settings",
    show: ALL,
    section: "admin",
  },
];

export function LeftNav() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const persona = usePersona(workspaceId);
  const { data: me } = useMe();
  const location = useLocation();

  const visible = ITEMS.filter((item) =>
    persona ? item.show.has(persona) : item.show.has("operator"),
  );

  const workItems = visible.filter((i) => i.section === "work");
  const orchItems = visible.filter((i) => i.section === "orchestration");
  const adminItems = visible.filter((i) => i.section === "admin");
  const showHands = persona ? HANDS_PERSONAS.has(persona) : false;

  const handsBase = `/workspaces/${workspaceId ?? ""}/hands`;
  const isHandsActive = location.pathname.startsWith(handsBase);

  const navCollapsed = useAppStore((s) => s.navCollapsed);
  const toggleNav = useAppStore((s) => s.toggleNav);

  return (
    <aside className="nav" data-testid="leftnav">
      {/* Hamburger row — sits in the normal flex flow so the menu items
          beneath shift down by one row. Centred when collapsed so it
          remains a single-click target in the narrow strip. */}
      <div className="nav-toggle-row">
        <button
          type="button"
          className="nav-collapse-toggle"
          onClick={toggleNav}
          aria-label={navCollapsed ? "expand navigation" : "collapse navigation"}
          title={navCollapsed ? "Expand navigation" : "Collapse navigation"}
          data-testid="leftnav-collapse-toggle"
        >
          <span className="nav-hamburger" aria-hidden="true">≡</span>
        </button>
      </div>
      {workItems.map((item) => (
        <NavItem key={item.key} item={item} workspaceId={workspaceId ?? ""} />
      ))}

      {orchItems.length > 0 ? (
        <>
          <div className="nav-section">Orchestration</div>
          {orchItems.map((item) => (
            <NavItem key={item.key} item={item} workspaceId={workspaceId ?? ""} />
          ))}
        </>
      ) : null}

      {showHands ? (
        <>
          <div className="nav-section">Hands Agents</div>
          <NavLink
            to={`${handsBase}/library`}
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            data-testid="navitem-hands-library"
          >
            <span className="ic"><Icon name="bot" /></span>
            <span>Agent Library</span>
          </NavLink>
          {(persona === "cxo" || persona === "owner") ? (
            <NavLink
              to={`${handsBase}/builder`}
              className={() => `nav-item ${location.pathname.startsWith(`${handsBase}/builder`) ? "active" : ""}`}
              data-testid="navitem-hands-builder"
            >
              <span className="ic"><Icon name="sparkle" /></span>
              <span>Agent Builder</span>
            </NavLink>
          ) : null}
          <NavLink
            to={`${handsBase}/my`}
            className={({ isActive }) => `nav-item ${isActive || (isHandsActive && !location.pathname.includes("/library") && !location.pathname.includes("/builder") && !location.pathname.includes("/agents")) ? "active" : ""}`}
            data-testid="navitem-hands-my"
          >
            <span className="ic"><Icon name="user" /></span>
            <span>My Hands</span>
          </NavLink>
          {(persona === "cxo" || persona === "owner") ? (
            <NavLink
              to={`/workspaces/${workspaceId ?? ""}/agents`}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid="navitem-agents"
            >
              <span className="ic"><Icon name="flow" /></span>
              <span>Integration &amp; LLM</span>
            </NavLink>
          ) : null}
        </>
      ) : null}

      {adminItems.length > 0 ? (
        <>
          <div className="nav-section">Workspace</div>
          {adminItems.map((item) => (
            <NavItem key={item.key} item={item} workspaceId={workspaceId ?? ""} />
          ))}
        </>
      ) : null}

      <div className="nav-foot">
        <div className="avatar" style={{ width: 24, height: 24, fontSize: 10 }}>
          {(me?.user.displayName ?? "?")
            .split(" ")
            .map((s) => s.charAt(0).toUpperCase())
            .join("")
            .slice(0, 2) || "?"}
        </div>
        <div className="who">
          <div>{me?.user.displayName ?? "—"}</div>
          <div className="role">
            <b>{persona ?? "—"}</b>
          </div>
        </div>
      </div>
    </aside>
  );
}

function NavItem({ item, workspaceId }: { item: NavSpec; workspaceId: string }) {
  const to = `/workspaces/${workspaceId}/${item.path}`;
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
      data-testid={`navitem-${item.key}`}
    >
      <span className="ic">
        <Icon name={item.icon} />
      </span>
      <span>{item.label}</span>
    </NavLink>
  );
}
